import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, MapPin, IndianRupee, Send, Mail, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { LinkedAmountPercent, amtToPct } from './booking/LinkedAmountPercent';
import { useBooking } from '../contexts/BookingContext';
import { useAuth } from '../contexts/AuthContext';
import { useCustomer } from '../contexts/CustomerContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { formatTime } from '../utils/timeSlots';

const getSectionSlotType = (section, facility) => {
  // If facility has courts, find the court type
  if (facility?.courts) {
    const court = facility.courts.find(c => c.id === section);
    if (court) {
      return court.court_type || court.sport || section;
    }
  }
  // Fallback for football
  if (section === 'full') return 'full';
  if (section === 'north' || section === 'south') return 'half';
  if (section === '5aside') return '5aside';
  return 'quarter';
};

const MultiSlotBookingDialog = ({ isOpen, onClose, slotInfo, facility }) => {
  const { addBooking } = useBooking();
  const { user, facility: authFacility } = useAuth();
  const { getCustomerByPhone } = useCustomer();
  
  // Use passed facility or fall back to auth context facility
  const currentFacility = facility || authFacility;
  
  // Check if this is SUFC facility
  const isSUFC = currentFacility?.id === 'facility_trp_sports' || 
                 currentFacility?.name?.toLowerCase()?.includes('south united') ||
                 user?.facility_id === 'facility_trp_sports';

  // Check if this is Sports Web - BCFC facility
  const isBCFC = currentFacility?.id === 'facility_sportsweb' ||
                 currentFacility?.name?.toLowerCase()?.includes('sportsweb') ||
                 currentFacility?.name?.toLowerCase()?.includes('sports web') ||
                 currentFacility?.name?.toLowerCase()?.includes('bcfc') ||
                 user?.facility_id === 'facility_sportsweb';
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    paymentStatus: 'pending',
    amountPaid: 0,
    bookingType: 'b2c',
    customRate: 0,
    discountPercent: 0, // Admin discount for B2C bookings
    discountAmount: 0, // ₹ amount is the money source of truth; % is derived
    paymentMethod: 'offline', // 'online' or 'offline'
    offlinePaymentMode: 'cash', // 'cash' | 'upi' | 'other' for offline collection
    onlinePayType: 'full', // 'full' or 'partial' (online only)
    onlinePartialPercent: 25,
    onlinePartialAmount: 0, // ₹ to charge now for online partial link
    sendEmailConfirmation: true // Default to send email confirmation
  });
  const [customerFound, setCustomerFound] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Customer autocomplete state
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const actualSlotType = getSectionSlotType(slotInfo?.section || 'full', currentFacility);
  
  // Get court name from facility if available
  const courtName = currentFacility?.courts?.find(c => c.id === slotInfo?.section)?.name || slotInfo?.section;

  // Calculate price on mount and when relevant fields change
  useEffect(() => {
    const calculatePrice = async () => {
      if (!slotInfo?.startTime || !slotInfo?.endTime) return;
      
      // For SUFC / BCFC internal bookings, price is always ₹0
      if (formData.bookingType === 'sufc' || formData.bookingType === 'bcfc') {
        setTotalPrice(0);
        setDiscountedPrice(0);
        return;
      }
      
      try {
        const params = {
          slot_type: actualSlotType,
          start_time: slotInfo.startTime,
          end_time: slotInfo.endTime,
          booking_type: formData.bookingType,
          date: slotInfo.date || new Date().toISOString().split('T')[0]
        };
        
        if ((formData.bookingType === 'b2b' || formData.bookingType === 'event') && formData.customRate > 0) {
          params.custom_rate = formData.customRate;
        }
        
        const result = await api.calculatePrice(params);
        const basePrice = result.total;
        setTotalPrice(basePrice);
        
        // Apply discount (₹ amount is the source of truth, clamped to the base price)
        if (formData.bookingType === 'b2c' && formData.discountAmount > 0) {
          setDiscountedPrice(basePrice - Math.min(formData.discountAmount, basePrice));
        } else {
          setDiscountedPrice(basePrice);
        }
      } catch (error) {
        console.error('Error calculating price:', error);
        // Fallback calculation
        const hours = (slotInfo.slots?.length || 2) * 0.5;
        const basePrice = hours * 5000; // Default rate
        setTotalPrice(basePrice);
        
        // Apply discount (₹ amount is the source of truth, clamped to the base price)
        if (formData.bookingType === 'b2c' && formData.discountAmount > 0) {
          setDiscountedPrice(basePrice - Math.min(formData.discountAmount, basePrice));
        } else {
          setDiscountedPrice(basePrice);
        }
      }
    };
    
    if (isOpen) {
      calculatePrice();
    }
  }, [slotInfo, formData.bookingType, formData.customRate, formData.discountAmount, actualSlotType, isOpen]);

  // Final total the customer owes = rack rate − discount. Partial payment is a slice of this.
  const finalTotal = (formData.bookingType === 'b2c' && formData.discountAmount > 0) ? discountedPrice : totalPrice;

  // Keep entered amounts valid when the base changes (e.g. discount lowers the final total).
  useEffect(() => {
    setFormData((fd) => {
      if (!totalPrice || totalPrice <= 0) return fd; // price not loaded yet — don't wipe prefilled values
      let next = fd;
      if ((fd.discountAmount || 0) > totalPrice) next = { ...next, discountAmount: totalPrice };
      if ((fd.onlinePartialAmount || 0) > finalTotal) next = { ...next, onlinePartialAmount: finalTotal };
      if ((fd.amountPaid || 0) > finalTotal) next = { ...next, amountPaid: finalTotal };
      return next;
    });
  }, [finalTotal, totalPrice]);

  // Handle customer auto-populate by phone
  useEffect(() => {
    const lookupCustomer = async () => {
      if (formData.customerPhone && formData.customerPhone.length === 10) {
        try {
          const customer = await getCustomerByPhone(formData.customerPhone);
          if (customer && !customerFound) {
            setFormData(prev => ({
              ...prev,
              customerName: customer.name,
              customerEmail: customer.email || ''
            }));
            setCustomerFound(true);
            toast({
              title: "Customer Found",
              description: `Loaded details for ${customer.name}. Total bookings: ${customer.total_bookings}, Total spent: ₹${customer.total_spent}`,
            });
          } else if (!customer && customerFound) {
            setCustomerFound(false);
          }
        } catch (error) {
          if (customerFound) setCustomerFound(false);
        }
      }
    };
    lookupCustomer();
  }, [formData.customerPhone, customerFound, getCustomerByPhone]);

  // Customer name autocomplete search
  useEffect(() => {
    const searchCustomers = async () => {
      if (formData.customerName.length < 2 || customerFound) {
        setCustomerSuggestions([]);
        setShowCustomerDropdown(false);
        return;
      }
      
      setSearchingCustomers(true);
      try {
        const results = await api.searchCustomersByName(formData.customerName);
        setCustomerSuggestions(results || []);
        setShowCustomerDropdown(results && results.length > 0);
      } catch (error) {
        console.error('Customer search error:', error);
        setCustomerSuggestions([]);
      } finally {
        setSearchingCustomers(false);
      }
    };
    
    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [formData.customerName, customerFound]);

  // Handle customer selection from dropdown
  const handleSelectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerName: customer.name || '',
      customerEmail: customer.email || '',
      customerPhone: customer.phone || ''
    }));
    setCustomerFound(true);
    setShowCustomerDropdown(false);
    setCustomerSuggestions([]);
    toast({
      title: "Customer Selected",
      description: `Loaded details for ${customer.name}`,
    });
  };

  if (!isOpen) return null;

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Determine if we're sending payment link (online method)
      const isOnlinePayment = formData.paymentMethod === 'online';
      
      // Use discounted price for B2C bookings with discount
      const finalAmount = formData.bookingType === 'b2c' && formData.discountAmount > 0 
        ? discountedPrice 
        : totalPrice;
      
      const amountPaid = isOnlinePayment ? 0 :
                         formData.paymentStatus === 'paid' ? finalAmount : 
                         (formData.paymentStatus === 'partial' || formData.paymentStatus === 'monthly') ? Math.min(formData.amountPaid, finalAmount) : 0;

      // Discount amount recorded (₹ is the source of truth)
      const discountAmount = formData.bookingType === 'b2c' && formData.discountAmount > 0
        ? Math.min(formData.discountAmount, totalPrice)
        : null;

      const bookingData = {
        facility_id: currentFacility?.id || null,
        date: slotInfo.date,
        start_time: slotInfo.startTime,
        end_time: slotInfo.endTime,
        slot_type: actualSlotType,
        section: slotInfo.section === 'full' ? null : slotInfo.section,
        court_id: slotInfo.section,  // Store the court ID
        sport: currentFacility?.courts?.find(c => c.id === slotInfo.section)?.sport || 'football',
        customer_name: formData.customerName,
        customer_email: formData.customerEmail || null,
        customer_phone: formData.customerPhone,
        booking_type: formData.bookingType,
        source: 'direct',  // Admin bookings are always 'direct'
        custom_rate: formData.customRate || null,
        amount: finalAmount,
        amount_paid: amountPaid,
        // Honour explicit Monthly billing choice. Other statuses derived server-side.
        payment_status: formData.paymentMethod === 'offline' && formData.paymentStatus === 'monthly'
          ? 'monthly'
          : undefined,
        // Discount fields
        discount_percent: formData.bookingType === 'b2c' && formData.discountAmount > 0 ? amtToPct(Math.min(formData.discountAmount, totalPrice), totalPrice) : null,
        discount_amount: discountAmount,
        // How offline money was collected (cash/upi/other) — feeds finance reconciliation
        payment_method: (formData.paymentMethod === 'offline' && ['paid', 'partial', 'monthly'].includes(formData.paymentStatus)) ? formData.offlinePaymentMode : undefined,
        // Email notification flag
        send_confirmation_email: formData.sendEmailConfirmation
      };

      const newBooking = await addBooking(bookingData);
      
      // Send payment link if online payment method selected
      if (isOnlinePayment && totalPrice > 0) {
        // Partial: charge the chosen amount now; the booking amount stays the full charge.
        const linkAmount = (formData.onlinePayType === 'partial' && formData.onlinePartialAmount > 0)
          ? Math.min(formData.onlinePartialAmount, finalAmount)
          : finalAmount;
        try {
          const linkResult = await api.createPaymentLink({
            booking_id: newBooking.id,
            amount: linkAmount,
            customer_name: formData.customerName,
            customer_email: formData.customerEmail || null,
            customer_phone: formData.customerPhone,
            description: linkAmount < finalAmount
              ? `Partial (${amtToPct(linkAmount, finalAmount)}%) payment for booking on ${slotInfo.date}`
              : `Payment for booking on ${slotInfo.date}`
          });
          
          toast({
            title: "Booking Created & Payment Link Sent!",
            description: (
              <div className="space-y-1">
                <p>Booking ID: {newBooking.id}</p>
                <p>Duration: {(slotInfo.slots?.length || 2) * 30} minutes</p>
                <p>Payment link sent to {formData.customerPhone}</p>
                <p className="text-xs text-gray-500 break-all">{linkResult.short_url}</p>
              </div>
            ),
          });
        } catch (linkError) {
          toast({
            title: "Booking Created (Payment Link Failed)",
            description: `Booking created but payment link failed: ${linkError.message}`,
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Booking Created Successfully!",
          description: (
            <div className="space-y-1">
              <p>Booking ID: {newBooking.id}</p>
              <p>Duration: {(slotInfo.slots?.length || 2) * 30} minutes</p>
              <p>Customer: {formData.customerName}</p>
              {amountPaid === 0 && <p className="text-yellow-600 text-sm">Payment pending (offline)</p>}
            </div>
          ),
        });
      }
      onClose();
    } catch (error) {
      toast({
        title: "Error Creating Booking",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Create Multi-Slot Booking</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6">
          {/* Slot Info */}
          <div className="bg-blue-50 p-4 rounded-lg mb-6 space-y-2">
            <div className="flex items-center text-sm">
              <Calendar className="h-4 w-4 mr-2 text-blue-600" />
              <span className="font-semibold">{new Date(slotInfo.date).toLocaleDateString('en-IN')}</span>
            </div>
            <div className="flex items-center text-sm">
              <Clock className="h-4 w-4 mr-2 text-blue-600" />
              <span className="font-semibold">
                {formatTime(slotInfo.startTime)} - {formatTime(slotInfo.endTime)}
              </span>
            </div>
            <div className="flex items-center text-sm">
              <MapPin className="h-4 w-4 mr-2 text-blue-600" />
              <span className="font-semibold capitalize">
                {slotInfo.section?.replace('-', ' ')} ({actualSlotType})
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-sm">
                <strong>Duration:</strong> {(slotInfo.slots?.length || 2) * 30} minutes ({slotInfo.slots?.length || 2} slots)
              </p>
              {formData.bookingType === 'b2c' && formData.discountAmount > 0 ? (
                <div className="mt-1">
                  <p className="text-sm text-gray-500 line-through">
                    Original: ₹{totalPrice}
                  </p>
                  <p className="text-lg font-bold text-[#0A2C93]">
                    <IndianRupee className="inline h-5 w-5" />
                    Final: ₹{discountedPrice}
                    <span className="ml-2 text-xs bg-[#00D650]/10 text-[#0A2C93] px-2 py-0.5 rounded">
                      {amtToPct(Math.min(formData.discountAmount, totalPrice), totalPrice)}% OFF
                    </span>
                  </p>
                </div>
              ) : (
                <p className="text-lg font-bold text-blue-600 mt-1">
                  <IndianRupee className="inline h-5 w-5" />
                  Total: ₹{totalPrice}
                </p>
              )}
            </div>
          </div>

          {/* Booking Form */}
          <form onSubmit={handleCreateBooking} className="space-y-4">
            <div>
              <Label htmlFor="bookingType">Booking Type</Label>
              <select
                id="bookingType"
                className="w-full p-2 border rounded"
                value={formData.bookingType}
                onChange={(e) => {
                  const newType = e.target.value;
                  const isInternal = newType === 'sufc' || newType === 'bcfc';
                  setFormData({ 
                    ...formData, 
                    bookingType: newType, 
                    discountPercent: 0,
                    discountAmount: 0,
                    // Auto-set for SUFC / BCFC internal bookings
                    amountPaid: isInternal ? 0 : formData.amountPaid,
                    paymentStatus: isInternal ? 'paid' : formData.paymentStatus
                  });
                }}
              >
                <option value="b2c">B2C (Individual Customer)</option>
                <option value="b2b">B2B (Corporate/Bulk)</option>
                <option value="event">Event (Custom Rate)</option>
                {isSUFC && (
                  <option value="sufc">SUFC (Internal - ₹0)</option>
                )}
                {isBCFC && (
                  <option value="bcfc">BCFC (Internal - ₹0)</option>
                )}
              </select>
              {formData.bookingType === 'sufc' && (
                <p className="text-xs text-blue-600 mt-1 font-semibold">
                  SUFC internal booking - Amount will be ₹0
                </p>
              )}
              {formData.bookingType === 'bcfc' && (
                <p className="text-xs text-blue-600 mt-1 font-semibold">
                  BCFC internal booking - Amount will be ₹0
                </p>
              )}
            </div>
            {/* Admin Discount for B2C bookings — enter ₹ OR % (linked) */}
            {formData.bookingType === 'b2c' && (
              <div>
                <Label className="text-sm font-semibold">Discount</Label>
                <LinkedAmountPercent
                  base={totalPrice}
                  amount={formData.discountAmount}
                  onChange={(amt) => setFormData({ ...formData, discountAmount: amt })}
                  amountLabel="Discount (₹)"
                  percentLabel="Discount (%)"
                  maxPercent={100}
                  testIdPrefix="multi-discount"
                />
                {formData.discountAmount > 0 && (
                  <p className="text-sm text-[#0A2C93] font-medium mt-1">
                    Save ₹{Math.min(formData.discountAmount, totalPrice)} · Final total ₹{discountedPrice}
                  </p>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="customerPhone">Phone (10 digits)</Label>
              <Input
                id="customerPhone"
                type="tel"
                required
                pattern="[0-9]{10}"
                maxLength="10"
                value={formData.customerPhone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setFormData({ ...formData, customerPhone: value });
                }}
                placeholder="9876543210"
              />
              {customerFound && (
                <p className="text-xs text-green-600 mt-1 font-semibold">✓ Existing customer - details auto-loaded</p>
              )}
            </div>
            <div className="relative">
              <Label htmlFor="customerName">Customer Name</Label>
              <Input
                id="customerName"
                required
                value={formData.customerName}
                onChange={(e) => {
                  setFormData({ ...formData, customerName: e.target.value });
                  if (customerFound && e.target.value !== formData.customerName) {
                    setCustomerFound(false);
                  }
                }}
                onFocus={() => {
                  if (customerSuggestions.length > 0) {
                    setShowCustomerDropdown(true);
                  }
                }}
                onBlur={() => {
                  // Delay closing to allow click on dropdown items
                  setTimeout(() => setShowCustomerDropdown(false), 200);
                }}
                placeholder="Start typing customer name..."
                autoComplete="off"
              />
              {customerFound && (
                <p className="text-xs text-green-600 mt-1 font-semibold">✓ Existing customer selected</p>
              )}
              {searchingCustomers && (
                <p className="text-xs text-gray-500 mt-1">Searching...</p>
              )}
              {/* Customer Autocomplete Dropdown */}
              {showCustomerDropdown && customerSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                  {customerSuggestions.map((customer, index) => (
                    <div
                      key={customer.id || index}
                      className="px-3 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectCustomer(customer);
                      }}
                    >
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      <div className="text-xs text-gray-500 flex gap-2">
                        <span>{customer.phone}</span>
                        {customer.email && <span>• {customer.email}</span>}
                        {customer.total_bookings > 0 && (
                          <span className="text-green-600">• {customer.total_bookings} bookings</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="customerEmail">Email</Label>
              <Input
                id="customerEmail"
                type="email"
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                placeholder="customer@example.com"
              />
            </div>
            {(formData.bookingType === 'b2b' || formData.bookingType === 'event') && (
              <div>
                <Label htmlFor="customRate">Custom Rate (₹/hour)</Label>
                <Input
                  id="customRate"
                  type="number"
                  value={formData.customRate}
                  onChange={(e) => setFormData({ ...formData, customRate: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter custom hourly rate"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.bookingType === 'b2b' 
                    ? 'B2B custom pricing - Enter the negotiated hourly rate'
                    : 'Event pricing - Enter the special event rate per hour'
                  }
                </p>
              </div>
            )}
            {/* Payment Collection Method */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Payment Collection Method</Label>
              <div className="grid grid-cols-2 gap-3">
                {/* Online Payment Option */}
                <div 
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.paymentMethod === 'online' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => setFormData({ 
                    ...formData, 
                    paymentMethod: 'online',
                    paymentStatus: 'pending'
                  })}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.paymentMethod === 'online' ? 'border-blue-500' : 'border-gray-400'
                    }`}>
                      {formData.paymentMethod === 'online' && (
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      )}
                    </div>
                    <span className="font-medium text-sm">Online</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    <Send className="inline h-3 w-3 mr-1" />
                    Payment link via SMS
                  </p>
                </div>
                
                {/* Offline Payment Option */}
                <div 
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.paymentMethod === 'offline' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                  onClick={() => setFormData({ 
                    ...formData, 
                    paymentMethod: 'offline'
                  })}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.paymentMethod === 'offline' ? 'border-green-500' : 'border-gray-400'
                    }`}>
                      {formData.paymentMethod === 'offline' && (
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      )}
                    </div>
                    <span className="font-medium text-sm">Offline</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    <IndianRupee className="inline h-3 w-3 mr-1" />
                    Cash / UPI / Other
                  </p>
                </div>
              </div>

              {/* Online: Full vs Partial payment link */}
              {formData.paymentMethod === 'online' && currentFacility?.partial_payment?.enabled && (
                <div className="mt-3 space-y-2" data-testid="multi-online-paytype">
                  <Label className="text-xs">Payment amount</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="multi-pay-full"
                      onClick={() => setFormData({ ...formData, onlinePayType: 'full' })}
                      className={`p-2 border-2 rounded-lg text-sm font-medium ${formData.onlinePayType !== 'partial' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    >Full</button>
                    <button
                      type="button"
                      data-testid="multi-pay-partial"
                      onClick={() => setFormData({ ...formData, onlinePayType: 'partial', onlinePartialAmount: formData.onlinePartialAmount || Math.round(finalTotal * (currentFacility?.partial_payment?.customer_percentage || 25) / 100) })}
                      className={`p-2 border-2 rounded-lg text-sm font-medium ${formData.onlinePayType === 'partial' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    >Partial</button>
                  </div>
                  {formData.onlinePayType === 'partial' && (
                    <LinkedAmountPercent
                      base={finalTotal}
                      amount={formData.onlinePartialAmount}
                      onChange={(amt) => setFormData({ ...formData, onlinePartialAmount: amt })}
                      amountLabel="Amount to pay now (₹)"
                      percentLabel="Percentage (%)"
                      maxPercent={99}
                      minAmount={Math.round(finalTotal * (currentFacility?.partial_payment?.min_admin_percentage || 10) / 100)}
                      invalidHint={`Minimum is ${currentFacility?.partial_payment?.min_admin_percentage || 10}% (₹${Math.round(finalTotal * (currentFacility?.partial_payment?.min_admin_percentage || 10) / 100)}).`}
                      showBalance
                      balanceLabel="due at venue"
                      testIdPrefix="multi-partial"
                    />
                  )}
                  <p className="text-xs text-gray-500">Min {currentFacility?.partial_payment?.min_admin_percentage || 10}%. Link is sent for the amount above; balance collected later.</p>
                </div>
              )}
              
              {/* Offline Payment Status */}
              {formData.paymentMethod === 'offline' && (
                <div className="mt-2">
                  <Label htmlFor="paymentStatus" className="text-xs">Payment Status</Label>
                  <select
                    id="paymentStatus"
                    className="w-full p-2 border rounded text-sm"
                    value={formData.paymentStatus}
                    onChange={(e) => setFormData({ ...formData, paymentStatus: e.target.value })}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              )}
              
              {/* How was offline money collected? (feeds finance reconciliation) */}
              {formData.paymentMethod === 'offline' && (formData.paymentStatus === 'paid' || formData.paymentStatus === 'partial' || formData.paymentStatus === 'monthly') && (
                <div className="mt-2">
                  <Label className="text-xs">Collected via</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {[['cash', 'Cash'], ['upi', 'UPI'], ['other', 'Other']].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        data-testid={`multi-offline-mode-${val}`}
                        onClick={() => setFormData({ ...formData, offlinePaymentMode: val })}
                        className={`p-2 border-2 rounded-lg text-sm font-medium ${formData.offlinePaymentMode === val ? 'border-green-500 bg-green-50 text-[#0A2C93]' : 'border-gray-200 text-gray-600'}`}
                      >{lbl}</button>
                    ))}
                  </div>
                </div>
              )}

              {formData.paymentMethod === 'offline' && (formData.paymentStatus === 'partial' || formData.paymentStatus === 'monthly') && (
                <div>
                  <Label className="text-xs font-semibold">Amount paid now</Label>
                  <LinkedAmountPercent
                    base={finalTotal}
                    amount={formData.amountPaid}
                    onChange={(amt) => setFormData({ ...formData, amountPaid: amt })}
                    amountLabel="Amount paid (₹)"
                    percentLabel="Percentage (%)"
                    maxPercent={100}
                    showBalance
                    balanceLabel="due at venue"
                    testIdPrefix="multi-offline-paid"
                  />
                </div>
              )}
              
              {/* Payment Info Display */}
              <div className={`p-2 rounded text-sm ${formData.paymentMethod === 'online' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600'}`}>
                {formData.paymentMethod === 'online' && (
                  <p><Send className="inline h-3 w-3 mr-1" />Payment link will be sent to {formData.customerPhone || 'customer'}</p>
                )}
                {formData.paymentMethod === 'offline' && formData.paymentStatus === 'pending' && (
                  <p>Payment to be collected offline</p>
                )}
                {formData.paymentMethod === 'offline' && formData.paymentStatus === 'paid' && (
                  <p className="text-green-600">✓ Payment already collected</p>
                )}
              </div>

              {/* Email Confirmation Option */}
              <div className="flex items-center space-x-2 p-2 bg-blue-50 rounded border border-blue-200">
                <input
                  type="checkbox"
                  id="sendEmailConfirmation"
                  checked={formData.sendEmailConfirmation}
                  onChange={(e) => setFormData({ ...formData, sendEmailConfirmation: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="sendEmailConfirmation" className="flex items-center text-sm cursor-pointer">
                  <Mail className="h-4 w-4 mr-2 text-blue-600" />
                  Send email confirmation
                </label>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? 'Creating...' : 'Create Booking'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MultiSlotBookingDialog;
